import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuizPerformanceChart = ({ dateRange }) => {
  const [viewMode, setViewMode] = useState('scores'); // scores or completion

  const mockScoreData = [
    { week: 'Sem 1', avgScore: 65, completionRate: 68 },
    { week: 'Sem 2', avgScore: 68, completionRate: 70 },
    { week: 'Sem 3', avgScore: 70, completionRate: 72 },
    { week: 'Sem 4', avgScore: 72, completionRate: 74 },
    { week: 'Sem 5', avgScore: 74, completionRate: 75 },
    { week: 'Sem 6', avgScore: 76.3, completionRate: 76.3 }
  ];

  const mockQuizTypeData = [
    { type: 'Aptitudes', avgScore: 78, attempts: 1250 },
    { type: 'Intérêts', avgScore: 82, attempts: 1180 },
    { type: 'Personnalité', avgScore: 75, attempts: 980 },
    { type: 'Compétences', avgScore: 73, attempts: 1420 },
    { type: 'Orientation', avgScore: 80, attempts: 1050 }
  ];

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-sm">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Icon name="TrendingUp" size={24} className="text-primary" />
          <div>
            <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
              Performance et Amélioration des Quiz
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              Trajectoires de progression et taux de complétion
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'scores' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('scores')}
            iconName="LineChart"
          >
            Scores
          </Button>
          <Button
            variant={viewMode === 'completion' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('completion')}
            iconName="BarChart3"
          >
            Par Type
          </Button>
        </div>
      </div>

      {viewMode === 'scores' ? (
        <div className="h-80 md:h-96">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={mockScoreData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorCompletion" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="week"
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
                domain={[60, 85]}
                label={{ value: 'Score (%)', angle: -90, position: 'insideLeft', style: { fontSize: '12px', fill: '#6B7280' } }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
                formatter={(value) => [`${value}%`]}
              />
              <Legend
                wrapperStyle={{ fontSize: '12px' }}
                formatter={(value) => {
                  const labels = {
                    avgScore: 'Score Moyen',
                    completionRate: 'Taux de Complétion'
                  };
                  return labels?.[value] || value;
                }}
              />
              <Area
                type="monotone"
                dataKey="avgScore"
                stroke="#2563EB"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorScore)"
              />
              <Area
                type="monotone"
                dataKey="completionRate"
                stroke="#10B981"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorCompletion)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-80 md:h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={mockQuizTypeData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="type"
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
                label={{ value: 'Score Moyen (%)', angle: -90, position: 'insideLeft', style: { fontSize: '12px', fill: '#6B7280' } }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
                formatter={(value, name) => {
                  if (name === 'avgScore') return [`${value}%`, 'Score Moyen'];
                  if (name === 'attempts') return [value, 'Tentatives'];
                  return [value];
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '12px' }}
                formatter={(value) => {
                  const labels = {
                    avgScore: 'Score Moyen',
                    attempts: 'Nombre de Tentatives'
                  };
                  return labels?.[value] || value;
                }}
              />
              <Bar dataKey="avgScore" fill="#2563EB" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Performance Insights */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-bold text-primary mb-1">76.3%</p>
          <p className="text-xs md:text-sm text-muted-foreground">Score Moyen</p>
        </div>
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-bold text-success mb-1">+11.3%</p>
          <p className="text-xs md:text-sm text-muted-foreground">Amélioration</p>
        </div>
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">5,880</p>
          <p className="text-xs md:text-sm text-muted-foreground">Quiz Complétés</p>
        </div>
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-bold text-amber-500 mb-1">82%</p>
          <p className="text-xs md:text-sm text-muted-foreground">Meilleur Type</p>
        </div>
      </div>
    </div>
  );
};

export default QuizPerformanceChart;